import Vue from 'vue'
import MaintenancePage from '~/components/Maintenance/MaintenancePage'
import {
	getMaintenanceHealthStatus,
	getRouteFullPath,
	getRetryAfterMsFromResponse
} from '~/utils/maintenance'

function getCurrentRoute(app, route) {
	if (app && app.router && app.router.currentRoute) {
		return app.router.currentRoute
	}

	return route
}

function getBrowserRoute() {
	return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

export function getRecoveryNavigationTarget(
	intendedRoute,
	currentRoute = getBrowserRoute()
) {
	if (!intendedRoute || intendedRoute === currentRoute) {
		return null
	}

	return intendedRoute
}

export function reloadAfterRecovery(intendedRoute) {
	const target = getRecoveryNavigationTarget(intendedRoute)

	if (target) {
		window.location.assign(target)
		return
	}

	window.location.reload()
}

export async function checkMaintenanceHealth({
	$axios,
	store,
	route,
	apiVanityUrl = '',
	onRecovered = reloadAfterRecovery
}) {
	const wasActive = Boolean(
		store.state.maintenance && store.state.maintenance.active
	)
	const intendedRoute = wasActive
		? store.state.maintenance.intendedRoute
		: getRouteFullPath(route)

	const health = await getMaintenanceHealthStatus($axios, apiVanityUrl)

	if (health.status === 'healthy') {
		if (wasActive) {
			await store.dispatch('maintenance/recover')
			onRecovered(intendedRoute)
		}

		return 'healthy'
	}

	if (health.status === 'maintenance') {
		await store.dispatch('maintenance/activate', {
			intendedRoute,
			retryAfterMs: getRetryAfterMsFromResponse(health.response)
		})

		return 'maintenance'
	}

	return 'unknown'
}

export function createMaintenanceController(context, options = {}) {
	const { app, store, $axios, $config, route } = context
	const mountOverlay = options.mountOverlay !== false
	const checkOnStart = options.checkOnStart !== false
	const onRecovered = options.onRecovered || reloadAfterRecovery
	let pollTimer = null
	let inFlightCheck = null
	let unsubscribe = null
	let mountedInstance = null

	function clearPoll() {
		if (pollTimer) {
			clearTimeout(pollTimer)
			pollTimer = null
		}
	}

	function schedulePoll() {
		clearPoll()

		if (!store.state.maintenance || !store.state.maintenance.active) {
			return
		}

		pollTimer = setTimeout(async () => {
			await runHealthCheck()
			schedulePoll()
		}, store.state.maintenance.retryAfterMs)
	}

	function runHealthCheck(routeOverride) {
		if (inFlightCheck) {
			return inFlightCheck
		}

		inFlightCheck = checkMaintenanceHealth({
			$axios,
			store,
			route: routeOverride || getCurrentRoute(app, route),
			apiVanityUrl: ($config && $config.apiVanityUrl) || '',
			onRecovered
		}).finally(() => {
			inFlightCheck = null
		})

		return inFlightCheck
	}

	function mountMaintenancePage() {
		if (!mountOverlay || mountedInstance || !document.body) {
			return
		}

		const MaintenancePageClass = Vue.extend(MaintenancePage)
		mountedInstance = new MaintenancePageClass({
			store
		}).$mount()

		document.body.appendChild(mountedInstance.$el)
	}

	function start() {
		mountMaintenancePage()

		unsubscribe = store.subscribe(mutation => {
			if (!mutation.type.startsWith('maintenance/')) {
				return
			}

			if (store.state.maintenance.active) {
				schedulePoll()
				return
			}

			clearPoll()
		})

		if (store.state.maintenance && store.state.maintenance.active) {
			schedulePoll()
		}

		if (checkOnStart) {
			runHealthCheck()
		}

		if (app && app.router) {
			app.router.afterEach(to => {
				if (store.state.maintenance && store.state.maintenance.active) {
					store.dispatch('maintenance/updateIntendedRoute', {
						intendedRoute: getRouteFullPath(to)
					})
					return
				}

				runHealthCheck(to)
			})
		}
	}

	function stop() {
		clearPoll()

		if (typeof unsubscribe === 'function') {
			unsubscribe()
		}

		if (mountedInstance) {
			mountedInstance.$destroy()

			if (mountedInstance.$el && mountedInstance.$el.parentNode) {
				mountedInstance.$el.parentNode.removeChild(mountedInstance.$el)
			}

			mountedInstance = null
		}
	}

	return {
		start,
		stop,
		runHealthCheck
	}
}

export default context => {
	createMaintenanceController(context).start()
}
